import { ALLOWED_UPLOAD_MIME_TYPES, MAX_UPLOAD_BYTES, isAllowedUpload } from '@feedback/shared';
import { strings } from '../strings';

/**
 * The allow-list, now IMPORTED rather than restated.
 *
 * This file used to hold its own copy under a comment saying it MUST match the
 * backend's exactly. It did match, and both were wrong about the same thing: a
 * browser reports no type at all for `.log`, `.har`, `.eml` and the rest of a
 * diagnostics bundle, so those were refused for having no type rather than for
 * being the wrong one. Two lists that must match are a list that will not — the
 * same failure ADR 0004 records for the parallel component trees.
 *
 * The property that mattered is unchanged: this is client-side UX (fail fast,
 * before spending an upload round-trip), not the source of truth, and the
 * backend re-validates independently. It just no longer re-states the values.
 */
export const ALLOWED_MIME_TYPES = ALLOWED_UPLOAD_MIME_TYPES;

export type AllowedMimeType = (typeof ALLOWED_UPLOAD_MIME_TYPES)[number];

/** 10 MB. Keep in sync with `strings.attachments.errors.tooLarge`'s wording. */
export const MAX_FILE_SIZE_BYTES = MAX_UPLOAD_BYTES;

export type FileValidationResult = { ok: true } | { ok: false; reason: string };

/**
 * Client-side gate a selected `File` must pass before it's added to the Attachments list (and,
 * later, before Task 9's submit flow ever calls `uploadFile` with it). Checked independently —
 * MIME type first, then size — so a caller inspecting a rejection knows exactly which rule
 * failed.
 *
 * The type check goes through `isAllowedUpload`, which resolves the file's real
 * type from its extension when the browser reported none. Judging `file.type`
 * directly is what rejected a `.log` on a machine with no registration for it.
 */
export function validateFile(file: File): FileValidationResult {
  if (!isAllowedUpload(file.type, file.name)) {
    return { ok: false, reason: strings.attachments.errors.unsupportedType };
  }
  if (file.size > MAX_FILE_SIZE_BYTES) {
    return { ok: false, reason: strings.attachments.errors.tooLarge };
  }
  return { ok: true };
}
